'use client'

import { useScreenSize } from '@/hooks/useScreenSize'
import { useCallback, useEffect, useRef } from 'react'
import { useDispatch, useSelector } from 'react-redux'

import { easeInOutCubic, interpolateColor } from '@/lib/features/animatedBackground/agentThemes'
import {
  completeTransition,
  saveBlobStates,
} from '@/lib/features/animatedBackground/animatedBackgroundSlice'
import {
  AnimationEngine,
  type AnimationEngineConfig,
} from '@/lib/features/animatedBackground/animationEngine'
import { animatedBackgroundDebugState } from '@/lib/features/animatedBackground/debugState'
import {
  AnimatedBackgroundRenderingService,
  type RenderingConfig,
} from '@/lib/features/animatedBackground/renderingService'
import type { RootState } from '@/lib/store'
import { useAnimatedBackgroundOverride } from './AnimatedBackgroundOverride'

// Animation constants
const HIGH_BLUR = 36.0
const MEDIUM_BLUR = 28.0
const LOW_BLUR = 16.0
const TRANSITION_DURATION_MS = 500
const BLUR_EASING_STRENGTH = 0.08
const BLUR_THRESHOLD = 0.1
const BLOB_STATE_SAVE_INTERVAL_MS = 2000

interface AnimatedBackgroundCanvasProps {
  className?: string
  debugMode?: boolean
}

export function AnimatedBackgroundCanvas({
  className = '',
  debugMode = false,
}: AnimatedBackgroundCanvasProps) {
  const canvasRef = useRef<HTMLCanvasElement>(null)
  const animationEngineRef = useRef<AnimationEngine | null>(null)
  const renderingServiceRef = useRef<AnimatedBackgroundRenderingService | null>(null)
  const animationIdRef = useRef<number | null>(null)
  const dimensionsRef = useRef({ width: 0, height: 0 })
  const lastSaveTimeRef = useRef(0)
  const currentBlurRef = useRef(MEDIUM_BLUR)
  const targetBlurRef = useRef(MEDIUM_BLUR)
  const transitionStartTimeRef = useRef<number | null>(null)
  const transitionStartColorsRef = useRef<string[]>([])

  // Refs for transition state (to avoid stale closure in animate callback)
  const isTransitioningRef = useRef(false)
  const colorsRef = useRef<typeof colors>([])
  const targetColorsRef = useRef<typeof targetColors>([])

  const dispatch = useDispatch()
  const screenSize = useScreenSize()
  const { override } = useAnimatedBackgroundOverride()

  const {
    isEnabled,
    speed: reduxSpeed,
    colors: reduxColors,
    performanceMode,
    savedBlobStates,
    lastDimensions,
    targetColors: reduxTargetColors,
    isTransitioning,
  } = useSelector((state: RootState) => state.animatedBackground)

  // Apply overrides if present (for tab-specific changes)
  const speed = override.speed ?? reduxSpeed
  const colors = override.colors ?? reduxColors
  const targetColors = override.colors ?? reduxTargetColors

  const getBlurAmount = useCallback((mode: string): number => {
    switch (mode) {
      case 'high':
        return HIGH_BLUR
      case 'medium':
        return MEDIUM_BLUR
      case 'low':
        return LOW_BLUR
      default:
        return MEDIUM_BLUR
    }
  }, [])

  useEffect(() => {
    targetBlurRef.current = getBlurAmount(performanceMode)
  }, [performanceMode, getBlurAmount])

  // Sync Redux state to refs for animate callback
  useEffect(() => {
    isTransitioningRef.current = isTransitioning
    colorsRef.current = colors
    targetColorsRef.current = targetColors
  }, [isTransitioning, colors, targetColors])

  const initializeServices = useCallback(() => {
    const dimensions = dimensionsRef.current

    if (dimensions.width === 0 || dimensions.height === 0) return

    if (animationEngineRef.current) return

    const physicsConfig: AnimationEngineConfig = {
      performanceMode,
      speed,
      bounds: dimensions,
    }

    animationEngineRef.current = new AnimationEngine(physicsConfig)

    const renderingConfig: RenderingConfig = {
      performanceMode,
      dimensions,
    }

    renderingServiceRef.current = new AnimatedBackgroundRenderingService(renderingConfig)

    if (canvasRef.current) {
      renderingServiceRef.current.initialize(canvasRef.current)
    }

    const dimensionThreshold = 0.3
    const widthDiff =
      lastDimensions.width > 0
        ? Math.abs(dimensions.width - lastDimensions.width) / dimensions.width
        : 1
    const heightDiff =
      lastDimensions.height > 0
        ? Math.abs(dimensions.height - lastDimensions.height) / dimensions.height
        : 1

    const canRestoreState =
      savedBlobStates.length > 0 &&
      widthDiff < dimensionThreshold &&
      heightDiff < dimensionThreshold

    animationEngineRef.current.initializeBlobs(
      colors,
      canRestoreState ? savedBlobStates : undefined
    )
  }, [colors, performanceMode, speed])

  const updateDimensions = useCallback(() => {
    if (typeof window === 'undefined') return

    const width = window.innerWidth
    const height = window.innerHeight

    const isMobile = /Android|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(
      navigator.userAgent
    )
    const isLowMemory = 'deviceMemory' in navigator && (navigator as any).deviceMemory <= 4
    const pixelRatio = window.devicePixelRatio || 1

    let scaleFactor = 1.0
    if (isLowMemory) {
      scaleFactor = 0.8
    }

    const effectivePixelRatio = Math.min(pixelRatio, isMobile ? 1.5 : 2.0)

    const newDimensions = {
      width: Math.floor((width * scaleFactor) / effectivePixelRatio),
      height: Math.floor((height * scaleFactor) / effectivePixelRatio),
    }

    dimensionsRef.current = newDimensions

    if (animationEngineRef.current) {
      animationEngineRef.current.updateConfig({ bounds: newDimensions })
    }

    if (renderingServiceRef.current) {
      renderingServiceRef.current.updateConfig({ dimensions: newDimensions })
    }
  }, [screenSize])

  const animate = useCallback(
    (currentTime: number) => {
      const animationEngine = animationEngineRef.current
      const renderingService = renderingServiceRef.current

      if (!animationEngine || !renderingService) {
        animationIdRef.current = requestAnimationFrame(animate)
        return
      }

      // Handle color transitions (read from refs to avoid stale closure)
      if (isTransitioningRef.current) {
        if (transitionStartTimeRef.current === null) {
          transitionStartTimeRef.current = currentTime
          transitionStartColorsRef.current = colorsRef.current.map((b) => b.color)
        }

        const transitionDuration = TRANSITION_DURATION_MS
        const elapsed = currentTime - transitionStartTimeRef.current
        const progress = Math.min(elapsed / transitionDuration, 1.0)
        const easedProgress = easeInOutCubic(progress)

        const interpolatedColors = colorsRef.current.map((blob, index) => {
          const startColor = transitionStartColorsRef.current[index] || blob.color
          const targetColor = targetColorsRef.current[index]?.color || blob.color
          return {
            ...blob,
            color: interpolateColor(startColor, targetColor, easedProgress),
          }
        })

        // Update animation engine directly without Redux dispatch for performance
        animationEngine.updateBlobColors(interpolatedColors)

        if (progress >= 1.0) {
          dispatch(completeTransition())
          transitionStartTimeRef.current = null
          transitionStartColorsRef.current = []
        }
      }

      const targetBlur = targetBlurRef.current
      const currentBlur = currentBlurRef.current
      const easingStrength = BLUR_EASING_STRENGTH

      if (Math.abs(targetBlur - currentBlur) > BLUR_THRESHOLD) {
        const delta = targetBlur - currentBlur
        const easedDelta = delta * (1 - Math.pow(1 - easingStrength, 3))
        currentBlurRef.current = currentBlur + easedDelta
      } else {
        currentBlurRef.current = targetBlur
      }

      const positions = animationEngine.update(currentTime)

      if (renderingService.needsGradientUpdate()) {
        renderingService.preRenderGradients(positions)
      }

      renderingService.render(positions)

      if (currentTime - lastSaveTimeRef.current > BLOB_STATE_SAVE_INTERVAL_MS) {
        const blobStates = animationEngine.getBlobStates()
        const dimensions = dimensionsRef.current

        if (blobStates.length > 0 && dimensions.width > 0 && dimensions.height > 0) {
          dispatch(
            saveBlobStates({
              blobs: blobStates,
              dimensions,
            })
          )
        }

        lastSaveTimeRef.current = currentTime
      }

      if (debugMode) {
        animatedBackgroundDebugState.updateData({
          ...animationEngine.getDebugInfo(),
          version: 'canvas',
        })
      }

      animationIdRef.current = requestAnimationFrame(animate)
    },
    [dispatch, debugMode]
  )

  const startAnimation = useCallback(() => {
    if (animationIdRef.current) {
      cancelAnimationFrame(animationIdRef.current)
    }
    animationIdRef.current = requestAnimationFrame(animate)
  }, [animate])

  const stopAnimation = useCallback(() => {
    if (animationIdRef.current) {
      cancelAnimationFrame(animationIdRef.current)
      animationIdRef.current = null
    }
  }, [])

  useEffect(() => {
    updateDimensions()
    const handleResize = () => updateDimensions()
    window.addEventListener('resize', handleResize)
    return () => window.removeEventListener('resize', handleResize)
  }, [updateDimensions])

  useEffect(() => {
    if (dimensionsRef.current.width > 0 && dimensionsRef.current.height > 0) {
      initializeServices()
    }
  }, [initializeServices])

  useEffect(() => {
    if (animationEngineRef.current) {
      animationEngineRef.current.updateConfig({
        performanceMode,
        speed,
        bounds: dimensionsRef.current,
      })
    }

    if (renderingServiceRef.current) {
      renderingServiceRef.current.updateConfig({
        performanceMode,
        dimensions: dimensionsRef.current,
      })
    }
  }, [performanceMode, speed])

  useEffect(() => {
    if (isEnabled && dimensionsRef.current.width > 0 && dimensionsRef.current.height > 0) {
      startAnimation()
    } else {
      stopAnimation()
    }

    return stopAnimation
  }, [isEnabled, startAnimation, stopAnimation])

  useEffect(() => {
    return () => {
      stopAnimation()

      if (animationEngineRef.current) {
        const blobStates = animationEngineRef.current.getBlobStates()
        const dimensions = dimensionsRef.current

        if (blobStates.length > 0 && dimensions.width > 0 && dimensions.height > 0) {
          dispatch(
            saveBlobStates({
              blobs: blobStates,
              dimensions,
            })
          )
        }
      }

      if (renderingServiceRef.current) {
        renderingServiceRef.current.dispose()
        renderingServiceRef.current = null
      }

      animationEngineRef.current = null
    }
  }, [dispatch, stopAnimation])

  if (!isEnabled) {
    return null
  }

  if (performanceMode === 'disabled') {
    const staticGradients =
      colors.length >= 3
        ? `
            radial-gradient(600px circle at 20% 30%, ${colors[0].color}66 0%, transparent 50%),
            radial-gradient(400px circle at 80% 70%, ${colors[1].color}66 0%, transparent 50%),
            radial-gradient(500px circle at 40% 80%, ${colors[2].color}44 0%, transparent 50%)
          `
        : `
            radial-gradient(600px circle at 20% 30%, #FD954366 0%, transparent 50%),
            radial-gradient(400px circle at 80% 70%, #F996CD66 0%, transparent 50%),
            radial-gradient(500px circle at 40% 80%, #FF7B5A44 0%, transparent 50%)
          `

    return (
      <div
        className="animated-background-static-bg"
        style={{
          width: '100vw',
          height: '100vh',
          position: 'fixed',
          top: 0,
          left: 0,
          pointerEvents: 'none',
          zIndex: 1,
          background: staticGradients,
          filter: 'blur(40px)',
          opacity: 0.7,
        }}
      />
    )
  }

  const getCurrentBlurStyle = () => {
    return `${currentBlurRef.current}px`
  }

  return (
    <canvas
      ref={canvasRef}
      width={dimensionsRef.current.width}
      height={dimensionsRef.current.height}
      className={`animated-background-canvas ${className}`}
      style={{
        width: '100vw',
        height: '100vh',
        position: 'fixed',
        top: 0,
        left: 0,
        pointerEvents: 'none',
        zIndex: 2,
        filter: `blur(${getCurrentBlurStyle()})`,
      }}
    />
  )
}
